import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { notify } from 'services';
import {
  useFetchInventoryImports,
  useUploadInventoryReport,
} from 'hooks/api/inventory-2';
import type { InventoryUploadResponse } from 'contracts/inventory-2/uploads';
import { Scaffold } from 'layouts';
import { DataTableV2 } from 'components/ui/DataTableV2';

import { useInventoryImportColumns } from './hooks';
import { UploadDataForm, UploadResult, uploadTypeName, type UploadDataPayload } from './components';

export const ImportFilesPage = () => {
  const { uploadInventoryReport } = useUploadInventoryReport();
  const { fetchInventoryImports } = useFetchInventoryImports();
  const [result, setResult] = React.useState<InventoryUploadResponse | null>(null);
  const queryClient = useQueryClient();
  const columns = useInventoryImportColumns();

  const importsQuery = useQuery({
    queryKey: ['fetch-inventory-imports'],
    queryFn: () => fetchInventoryImports({ limit: 50 }),
    onError: (error: Error) => {
      notify('error', error?.message);
    },
  });

  const refetchImports = async () => {
    await queryClient.invalidateQueries(['fetch-inventory-imports']);
  };

  const uploadMutation = useMutation({
    mutationFn: uploadInventoryReport,
    onSuccess: async (data, variables) => {
      setResult(data);
      if (data.changed === false) {
        notify('warning', `O conteúdo de ${uploadTypeName(variables.uploadType)} já estava gravado.`);
      } else {
        notify('success', `Planilha de ${uploadTypeName(variables.uploadType)} enviada.`);
      }
      await refetchImports();
    },
    onError: async (error: Error) => {
      setResult(null);
      notify('error', error?.message);
      await refetchImports();
    },
  });

  const onUploadDataFormSubmit = React.useCallback((formData: UploadDataPayload) => {
    setResult(null);
    uploadMutation.mutate(formData);
  }, [uploadMutation]);

  return (
    <Scaffold>
      <h2 className="font-GilroySemibold text-gray-600 mb-4">
        Importar planilhas
      </h2>
      <UploadDataForm
        onSubmit={onUploadDataFormSubmit}
        loading={uploadMutation.isLoading}
      />
      {result && <UploadResult result={result} />}
      <h2 className={'font-GilroySemibold text-gray-600 mt-4'}>
        Histórico de importações
      </h2>
      <DataTableV2
        data={importsQuery.data ?? []}
        isLoading={importsQuery.isLoading}
        columns={columns}
      />
    </Scaffold>
  );
};
